import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  ArrowLeft, 
  Bell,
  BellRing,
  Plus,
  Trash2,
  TrendingUp,
  TrendingDown,
  Loader2,
  CheckCircle2
} from 'lucide-react';
import Layout from '../components/Layout';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PriceAlerts() {
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const [alerts, setAlerts] = useState({ active: [], triggered: [] });
  const [stocks, setStocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [newAlert, setNewAlert] = useState({
    symbol: '',
    target_price: '',
    alert_type: 'above',
    notes: ''
  });
  const [selectedStock, setSelectedStock] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [alertsRes, stocksRes] = await Promise.all([
        axios.get(`${API}/alerts`),
        axios.get(`${API}/stocks`)
      ]);
      setAlerts(alertsRes.data);
      setStocks(stocksRes.data.stocks);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (newAlert.symbol && stocks.length > 0) {
      const stock = stocks.find(s => s.symbol === newAlert.symbol);
      setSelectedStock(stock);
    } else {
      setSelectedStock(null);
    }
  }, [newAlert.symbol, stocks]);

  const handleCreateAlert = async () => {
    if (!newAlert.symbol || !newAlert.target_price) {
      toast.error(t('Please fill all required fields', 'Tafadhali jaza sehemu zote zinazohitajika'));
      return;
    }

    setIsSubmitting(true);
    try {
      await axios.post(`${API}/alerts`, {
        symbol: newAlert.symbol,
        target_price: parseFloat(newAlert.target_price),
        condition: newAlert.alert_type,  // Backend expects 'condition' field
        notes: newAlert.notes || null
      });
      toast.success(t('Price alert created!', 'Tahadhari ya bei imeundwa!'));
      setIsCreateDialogOpen(false);
      setNewAlert({ symbol: '', target_price: '', alert_type: 'above', notes: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('Failed to create alert', 'Imeshindwa kuunda tahadhari'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteAlert = async (alertId) => {
    try {
      await axios.delete(`${API}/alerts/${alertId}`);
      toast.success(t('Alert deleted', 'Tahadhari imefutwa'));
      fetchData();
    } catch (error) {
      toast.error(t('Failed to delete alert', 'Imeshindwa kufuta tahadhari'));
    }
  };

  const formatCurrency = (amount) => {
    return amount.toLocaleString();
  };

  const getPercentToTarget = (current, target, type) => {
    const diff = ((target - current) / current) * 100;
    return type === 'above' ? diff : -diff;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="page-enter space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(-1)}
              className="mb-2 -ml-2"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              {t('Back', 'Rudi')}
            </Button>
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground">
              {t('Price Alerts', 'Tahadhari za Bei')}
            </h1>
            <p className="text-muted-foreground">
              {t('Get notified when stocks reach your target price', 'Pokea taarifa hisa zinapofikia bei yako unayotaka')}
            </p>
          </div>
          <Button 
            onClick={() => setIsCreateDialogOpen(true)}
            className="rounded-full"
            data-testid="create-alert-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            {t('New Alert', 'Tahadhari Mpya')}
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-4">
          <Card className="border-border/50">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <Bell className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">{alerts.active.length}</p>
                <p className="text-sm text-muted-foreground">{t('Active Alerts', 'Tahadhari Hai')}</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-border/50">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center">
                <BellRing className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">{alerts.triggered.length}</p>
                <p className="text-sm text-muted-foreground">{t('Triggered', 'Zimefanya Kazi')}</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Alerts Tabs */}
        <Tabs defaultValue="active" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="active" data-testid="active-alerts-tab">
              {t('Active', 'Hai')} ({alerts.active.length})
            </TabsTrigger>
            <TabsTrigger value="triggered" data-testid="triggered-alerts-tab">
              {t('Triggered', 'Zimefanya Kazi')} ({alerts.triggered.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="active" className="mt-4">
            {alerts.active.length > 0 ? (
              <div className="space-y-3">
                {alerts.active.map((alert) => (
                  <Card key={alert.id} className="border-border/50">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div 
                          className="flex items-center gap-4 cursor-pointer"
                          onClick={() => navigate(`/stocks/${alert.symbol}`)}
                        >
                          <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                            alert.alert_type === 'above' ? 'bg-green-500/10' : 'bg-red-500/10'
                          }`}>
                            {alert.alert_type === 'above' ? (
                              <TrendingUp className="w-6 h-6 text-green-600" />
                            ) : (
                              <TrendingDown className="w-6 h-6 text-red-600" />
                            )}
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="font-semibold text-foreground">{alert.symbol}</p>
                              <span className={`text-xs px-2 py-0.5 rounded-full ${
                                alert.alert_type === 'above' 
                                  ? 'bg-green-100 text-green-700' 
                                  : 'bg-red-100 text-red-700'
                              }`}>
                                {alert.alert_type === 'above' 
                                  ? t('Above', 'Juu ya')
                                  : t('Below', 'Chini ya')
                                }
                              </span>
                            </div>
                            <p className="text-sm text-muted-foreground">{alert.stock_name}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <p className="font-semibold text-foreground">
                              {t('Target:', 'Lengo:')} TZS {formatCurrency(alert.target_price)}
                            </p>
                            <p className="text-sm text-muted-foreground">
                              {t('Current:', 'Sasa:')} TZS {formatCurrency(alert.current_price)}
                            </p>
                            <p className={`text-xs font-medium ${
                              getPercentToTarget(alert.current_price, alert.target_price, alert.alert_type) > 0 
                                ? 'text-green-600' 
                                : 'text-muted-foreground'
                            }`}>
                              {getPercentToTarget(alert.current_price, alert.target_price, alert.alert_type).toFixed(1)}% {t('to target', 'hadi lengo')}
                            </p>
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-muted-foreground hover:text-destructive"
                            onClick={() => handleDeleteAlert(alert.id)}
                            data-testid={`delete-alert-${alert.symbol}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                      {alert.notes && (
                        <p className="text-sm text-muted-foreground mt-2 pl-16">
                          {alert.notes}
                        </p>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <Card className="border-dashed">
                <CardContent className="p-12 text-center">
                  <Bell className="w-16 h-16 text-muted-foreground/30 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-foreground mb-2">
                    {t('No Active Alerts', 'Hakuna Tahadhari Hai')}
                  </h3>
                  <p className="text-muted-foreground mb-6">
                    {t('Create your first price alert to get notified when a stock reaches your target', 'Unda tahadhari yako ya kwanza ya bei kupokea taarifa hisa inapofikia lengo lako')}
                  </p>
                  <Button onClick={() => setIsCreateDialogOpen(true)} className="rounded-full">
                    <Plus className="w-4 h-4 mr-2" />
                    {t('Create Alert', 'Unda Tahadhari')}
                  </Button>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="triggered" className="mt-4">
            {alerts.triggered.length > 0 ? (
              <div className="space-y-3">
                {alerts.triggered.map((alert) => (
                  <Card key={alert.id} className="border-green-200 bg-green-50/50">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div 
                          className="flex items-center gap-4 cursor-pointer"
                          onClick={() => navigate(`/stocks/${alert.symbol}`)}
                        >
                          <div className="w-12 h-12 rounded-xl bg-green-500/20 flex items-center justify-center">
                            <CheckCircle2 className="w-6 h-6 text-green-600" />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="font-semibold text-foreground">{alert.symbol}</p>
                              <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700">
                                {t('Triggered', 'Imefanya Kazi')}
                              </span>
                            </div>
                            <p className="text-sm text-muted-foreground">{alert.stock_name}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <p className="font-semibold text-foreground">
                              {t('Target:', 'Lengo:')} TZS {formatCurrency(alert.target_price)}
                            </p>
                            <p className="text-sm text-muted-foreground">
                              {t('Current:', 'Sasa:')} TZS {formatCurrency(alert.current_price)}
                            </p>
                            {alert.triggered_at && (
                              <p className="text-xs text-green-600">
                                {new Date(alert.triggered_at).toLocaleDateString()}
                              </p>
                            )}
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-muted-foreground hover:text-destructive"
                            onClick={() => handleDeleteAlert(alert.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <Card className="border-dashed">
                <CardContent className="p-12 text-center">
                  <CheckCircle2 className="w-16 h-16 text-muted-foreground/30 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-foreground mb-2">
                    {t('No Triggered Alerts', 'Hakuna Tahadhari Zilizofanya Kazi')}
                  </h3>
                  <p className="text-muted-foreground">
                    {t('Your triggered alerts will appear here', 'Tahadhari zako zilizofanya kazi zitaonekana hapa')}
                  </p>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Create Alert Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>
              {t('Create Price Alert', 'Unda Tahadhari ya Bei')}
            </DialogTitle>
            <DialogDescription>
              {t('Get notified when a stock reaches your target price', 'Pokea taarifa hisa inapofikia bei yako unayotaka')}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            {/* Stock Selection */}
            <div className="grid gap-2">
              <Label>{t('Select Stock', 'Chagua Hisa')} *</Label>
              <Select 
                value={newAlert.symbol} 
                onValueChange={(value) => setNewAlert({...newAlert, symbol: value})}
              >
                <SelectTrigger data-testid="alert-stock-select">
                  <SelectValue placeholder={t('Choose a stock', 'Chagua hisa')} />
                </SelectTrigger>
                <SelectContent>
                  {stocks.map(stock => (
                    <SelectItem key={stock.symbol} value={stock.symbol}>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold">{stock.symbol}</span>
                        <span className="text-muted-foreground">- TZS {formatCurrency(stock.current_price)}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Selected Stock Info */}
            {selectedStock && (
              <div className="p-3 bg-muted/50 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-foreground">{selectedStock.name}</p>
                    <p className="text-sm text-muted-foreground">{selectedStock.sector}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-foreground">TZS {formatCurrency(selectedStock.current_price)}</p>
                    <p className={`text-xs ${selectedStock.ytd_change >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {selectedStock.ytd_change >= 0 ? '+' : ''}{selectedStock.ytd_change.toFixed(2)}% YTD
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Alert Type */}
            <div className="grid gap-2">
              <Label>{t('Alert When Price Is', 'Tahadhari Bei Inapokuwa')} *</Label>
              <Select 
                value={newAlert.alert_type} 
                onValueChange={(value) => setNewAlert({...newAlert, alert_type: value})}
              >
                <SelectTrigger data-testid="alert-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="above">
                    <div className="flex items-center gap-2">
                      <TrendingUp className="w-4 h-4 text-green-600" />
                      <span>{t('Above target price', 'Juu ya bei lengwa')}</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="below">
                    <div className="flex items-center gap-2">
                      <TrendingDown className="w-4 h-4 text-red-600" />
                      <span>{t('Below target price', 'Chini ya bei lengwa')}</span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Target Price */}
            <div className="grid gap-2">
              <Label htmlFor="target-price">{t('Target Price (TZS)', 'Bei Lengwa (TZS)')} *</Label>
              <Input
                id="target-price"
                type="number"
                placeholder={selectedStock ? `Current: ${formatCurrency(selectedStock.current_price)}` : 'e.g. 3000'}
                value={newAlert.target_price}
                onChange={(e) => setNewAlert({...newAlert, target_price: e.target.value})}
                min="0"
                step="any"
                data-testid="alert-price-input"
              />
            </div>

            {/* Notes */}
            <div className="grid gap-2">
              <Label htmlFor="alert-notes">{t('Notes (Optional)', 'Maelezo (Si lazima)')}</Label>
              <Input
                id="alert-notes"
                placeholder={t('e.g. Buy when price drops', 'mfano. Nunua bei ikishuka')}
                value={newAlert.notes}
                onChange={(e) => setNewAlert({...newAlert, notes: e.target.value})}
                data-testid="alert-notes-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsCreateDialogOpen(false)}
              disabled={isSubmitting}
            >
              {t('Cancel', 'Ghairi')}
            </Button>
            <Button
              onClick={handleCreateAlert}
              disabled={isSubmitting || !newAlert.symbol || !newAlert.target_price}
              data-testid="confirm-create-alert-btn"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t('Creating...', 'Inaunda...')}
                </>
              ) : (
                <>
                  <Bell className="w-4 h-4 mr-2" />
                  {t('Create Alert', 'Unda Tahadhari')}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
